import * as Yup from "yup";

import { Modal, OfficeButton } from "components";
import {
  UpdateInputOffer,
  UpdateInputOfferVariables,
} from "_graphql/mutation/__generated__/UpdateInputOffer";
import {
  UpdateKEKInputOffer,
  UpdateKEKInputOfferVariables,
} from "_graphql/kek/mutation/__generated__/UpdateKEKInputOffer";
import { useMutation, useReactiveVar } from "@apollo/client";

import { LocationGenerics } from "router/location";
import { OfferKEKForm } from "components/forms";
import _ from "lodash";
import { currentUserVar } from "_graphql/cache/auth";
import toast from "react-hot-toast";
import { updateKEKPolicy } from "_graphql/kek/mutation/index.kek";
import { updateOffer } from "_graphql/visal/mutations/offers";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useKEKOffer } from "hooks/data/use-offers";
import { useSearch } from "react-location";
import { wrapClick } from "utils";

export const validationSchema = Yup.object().shape({
  class_of_business_id: Yup.object().required("Business is required"),
  offer_details: Yup.array().notRequired(),
  insurer_id: Yup.string().required("Insurer is required"),
  premium: Yup.number()
    .min(0, "Number must be greater than 0")
    .typeError("Must be a number")
    .required("Premium is required"),
  policy_number: Yup.string().required("Policy Number is required"),
  insured_by: Yup.string().required("Insured is required"),
  sum_insured: Yup.number()
    .min(0, "Number must be greater than 0")
    .typeError("Must be a number")
    .required("Sum Insured is required"),
  rate: Yup.number()
    .min(0, "Number must be greater than 0")
    .typeError("Must be a number")
    .required("Rate is required"),
  facultative_offer: Yup.number()
    .min(0, "Number must be greater than 0")
    .typeError("Must be a number")
    .required("Facultative Offer is required"),
  commission: Yup.number()
    .min(0, "Number must be greater than 0")
    .typeError("Must be a number")
    .required("Commission is required"),
  brokerage: Yup.number()
    .min(0, "Number must be greater than 0")
    .typeError("Must be a number")
    .required("Brokerage is required"),
  currency: Yup.string().required("Currency is required"),
  period_of_insurance_from: Yup.date().required(
    "Insurance Start Date is required"
  ),
  period_of_insurance_to: Yup.date()
    .min(
      Yup.ref("period_of_insurance_from"),
      "End Date must be greater than Start Date"
    )
    .required("Insurance End Date is required"),
  comment: Yup.string().notRequired(),
});

export default function UpdateKEKOfferContainer({
  open,
  setOpen,
  refetch,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}) {
  const searchParams = useSearch<LocationGenerics>();
  const user = useReactiveVar(currentUserVar);
  const { loading: fetching, offer } = useKEKOffer({
    offer_id: searchParams.id || "",
  });
  const [create, { loading }] = useMutation<
    UpdateKEKInputOffer,
    UpdateKEKInputOfferVariables
  >(updateKEKPolicy, {
    refetchQueries: ["Offers"],
  });

  const form = useFormik<any>({
    initialValues: {
      createForRetroCedent: false,
      include_nkrol_comment: false,
      addExchangeRate: false,
      class_of_business_id: "",
      offer_details: [],
      insurer_id: "",
      premium: "",
      policy_number: "",
      insured_by: "",
      sum_insured: "",
      rate: "",
      facultative_offer: "",
      commission: "",
      brokerage: "",
      currency: "",
      period_of_insurance_from: "",
      period_of_insurance_to: "",
      comment: "",
      old_class_of_business_id: "",
      old_offer_detail: [],
      reinsurer_id: "",
      business_state: "",
      co_insurance_share: "",
      ex_currency: "",
      ex_rate: "",
      information_comment: null,
      payment_type: "payable_in_full",
    },
    validationSchema: validationSchema,
    onSubmit: async (variables) => {
      await create({
        variables: {
          offerDetailId: offer?.offer_detail?.offer_detail_id || "",
          offerId: offer?.offer_id || "",
          offerInput: {
            brokerage: parseFloat(variables.brokerage),
            insurer_id: variables.insurer_id,
            premium: parseFloat(variables.premium),
            policy_number: variables.policy_number,
            offer_details: JSON.stringify(variables.offer_details),
            class_of_business_id:
              variables.class_of_business_id?.class_of_business_id,
            employee_id: user?.employee?.employee_id ?? "",
            rate: parseFloat(variables.rate),
            commission: parseFloat(variables.commission),
            facultative_offer: parseFloat(variables.facultative_offer),
            sum_insured: parseFloat(variables.sum_insured),
            insured_by: variables.insured_by,
            currency: variables.currency,
            offer_comment: variables.comment,
            period_of_insurance_from: variables.period_of_insurance_from,
            period_of_insurance_to: variables.period_of_insurance_to,
            co_insurance_share: parseFloat(variables.co_insurance_share),
            business_state: variables.business_state,
            ex_currency: variables.addExchangeRate
              ? variables.ex_currency
              : null,
            ex_rate: variables.addExchangeRate
              ? parseFloat(variables.ex_rate)
              : null,
            information_comment: variables.information_comment,
            payment_type: {
              [variables.payment_type]:
                variables.payment_type === "payable_in_full"
                  ? "Facultative Premium payable in full at inception"
                  : variables.no_of_installments,
            },
          },
        },
      }).then(({ data }) => {
        if (data?.updateInputOffer) {
          toast.success("Offer updated Successfully");
          refetch?.();
          form.resetForm();
        } else {
          toast.success("Could not update Offer");
        }
      });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  useEffect(() => {
    if (offer) {
      form.setValues({
        class_of_business_id: offer.classofbusiness,
        old_class_of_business_id: offer.classofbusiness?.class_of_business_id,
        offer_details: JSON.parse(offer.offer_detail?.offer_details || "[]"),
        old_offer_detail: JSON.parse(offer.offer_detail?.offer_details || "[]"),
        insurer_id: offer.insurer?.insurer_id,
        premium: offer.premium,
        policy_number: offer?.offer_detail?.policy_number,
        insured_by: offer?.offer_detail?.insured_by,
        sum_insured: offer.sum_insured,
        rate: offer.rate,
        facultative_offer: offer.facultative_offer,
        commission: offer.commission,
        brokerage: offer.brokerage,
        currency: offer.offer_detail?.currency,
        period_of_insurance_from: offer.offer_detail?.period_of_insurance_from,
        period_of_insurance_to: offer.offer_detail?.period_of_insurance_to,
        comment: offer?.offer_detail?.offer_comment || "",

        reinsurer_id: offer?.offer_retrocedent?.reinsurer?.reinsurer_id || "",
        business_state: offer?.business_state || "",
        co_insurance_share: offer?.co_insurance_share || "",
        ex_currency: offer?.exchange_rate?.ex_currency || "",
        ex_rate: offer?.exchange_rate?.ex_rate || "",
        include_nkrol_comment: Boolean(
          offer?.offer_detail?.information_comment
        ),
        information_comment: offer?.offer_detail?.information_comment || "",
        payment_type:
          Object.keys(
            JSON.parse(offer?.offer_detail?.payment_type || "{}")
          )?.at(0) || "payable_in_full",
        no_of_installments:
          Object.values(
            JSON.parse(offer?.offer_detail?.payment_type || "{}")
          )?.at(0) || 1,
        addExchangeRate: Boolean(offer?.exchange_rate?.ex_currency),
        createForRetroCedent: Boolean(
          offer?.offer_retrocedent?.reinsurer?.reinsurer_id
        ),
      });
    }
  }, [offer]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      size="4xl"
      loading={fetching}
      title="Update Offer"
      description="Provide the details to update offer"
      renderActions={() => (
        <>
          <OfficeButton
            disabled={loading}
            onClick={wrapClick(form.handleSubmit)}
          >
            {loading ? "Updating Offer..." : "Update Offer"}
          </OfficeButton>
        </>
      )}
    >
      <OfferKEKForm form={form} />
    </Modal>
  );
}
